// ✅ FILE: src/pages/about/MissionVision.jsx
// FIXES YOU ASKED FOR:
// 1) ✅ "Become a Partner" button on HERO is now the SAME style as your footer partner button (BLUE).
// 2) ✅ Mission + Vision cards are now SMALLER, cleaner, and fit perfectly (side-by-side on lg screens).
// 3) ✅ Images remain directly under: Community-led / Accountable delivery / Sustainable impact
// 4) ✅ Pillar "Human Rights" -> "Sustainable Agriculture"

import { Target, Eye, Award, Sparkles, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Link } from "react-router-dom";
import "./mission-vision-premium.css";

const pillars = [
  { title: "Humanitarian Relief", desc: "Emergency response and welfare support" },
  { title: "Education Support", desc: "Child development and learning initiatives" },
  { title: "Sustainable Agriculture", desc: "Food security and livelihood resilience" }, // ✅ CHANGED
  { title: "Empowerment", desc: "Skills and capacity building programs" },
  { title: "Technology Inclusion", desc: "Digital literacy and tech access" },
  { title: "Climate Action", desc: "Environmental awareness and action" },
];

export const MissionVision = () => {
  return (
    <div data-testid="mission-vision-page" className="bg-white text-stone-900">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 md:py-36 text-white">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/assets/mission-hero.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-stone-950/90 via-blue-950/75 to-black/70" />

        <div className="absolute -top-20 -left-20 w-[280px] h-[280px] rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-10 w-[260px] h-[260px] rounded-full bg-cyan-400/15 blur-3xl" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium backdrop-blur-sm mb-6">
              <Sparkles className="w-4 h-4 text-amber-300" />
              About GLGIF
            </span>

            <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Mission & Vision
            </h1>

            <p className="text-lg md:text-xl text-stone-200 max-w-2xl leading-relaxed">
              Our guiding purpose and the future we’re building for vulnerable communities
              across Nigeria and beyond.
            </p>

            {/* ✅ BUTTONS: Keep your exact style language */}
            <div className="mt-8 flex flex-wrap gap-4">
              {/* Volunteer (Amber) */}
              <Button
                asChild
                className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-lg"
              >
                <Link to="/get-involved#volunteer" data-testid="mv-hero-volunteer-btn">
                  Become a Volunteer
                </Link>
              </Button>

              {/* ✅ Partner should match footer style (Blue) */}
              <Button
                asChild
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg"
              >
                <Link to="/get-involved#partner" data-testid="mv-hero-partner-btn">
                  Become a Partner
                </Link>
              </Button>

              {/* Donate (White) */}
              <Button
                asChild
                className="bg-white text-blue-900 hover:bg-white/90 rounded-full px-8 py-6 text-lg"
              >
                <Link to="/get-involved#donate" data-testid="mv-hero-donate-btn">
                  Donate Now
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO + CHIPS + IMAGES + SMALLER CARDS */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Left column */}
            <div className="lg:col-span-5">
              <p className="font-accent text-xl text-amber-700 mb-4">What Guides Us</p>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-stone-900 mb-5 leading-tight">
                We respond fast — and we build systems that last.
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed">
                GLGIF combines urgent support with long-term transformation — protecting dignity,
                restoring hope, and strengthening families through practical, community-led solutions.
              </p>

              {/* Chips */}
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-stone-100 border border-stone-200 px-4 py-2 text-sm text-stone-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  Community-led
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-stone-100 border border-stone-200 px-4 py-2 text-sm text-stone-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
                  Accountable delivery
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-stone-100 border border-stone-200 px-4 py-2 text-sm text-stone-700">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  Sustainable impact
                </span>
              </div>

              {/* ✅ Images directly under chips */}
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-sm group">
                  <img
                    src="/assets/whatwedo.png"
                    alt="Community support initiative"
                    className="w-full h-[190px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-semibold">Community Support</p>
                    <p className="text-white/80 text-sm">Relief and protection that restores dignity.</p>
                  </div>
                </div>

                <div className="relative rounded-2xl overflow-hidden border border-stone-200 shadow-sm group">
                  <img
                    src="/assets/about-main.png"
                    alt="Education and empowerment outreach"
                    className="w-full h-[190px] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <p className="text-white font-semibold">Education & Empowerment</p>
                    <p className="text-white/80 text-sm">Opportunity pathways for vulnerable lives.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column: ✅ Smaller mission/vision cards that fit perfectly */}
            <div className="lg:col-span-7">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Mission (small, tight) */}
                <Card
                  className="rounded-3xl border border-stone-200 shadow-sm hover:shadow-xl transition-all overflow-hidden"
                  data-testid="mission-card"
                >
                  <CardContent className="p-0">
                    <div className="p-7 md:p-8 bg-gradient-to-br from-amber-50 via-white to-white">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center shrink-0">
                          <Target className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                          <p className="text-xs tracking-[0.2em] uppercase text-amber-700 font-semibold mb-2">
                            Our Mission
                          </p>
                          <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-3 leading-snug">
                            Improve lives and protect human dignity.
                          </h3>
                          <p className="text-stone-600 leading-relaxed text-sm md:text-[15px]">
                            To deliver humanitarian relief, education support, empowerment, technology inclusion,
                            climate action, sustainable agriculture, and community-based interventions.
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 space-y-2.5">
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-amber-700 mt-0.5" />
                          <span>Immediate relief with lasting outcomes</span>
                        </div>
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-amber-700 mt-0.5" />
                          <span>Dignity-centered support systems</span>
                        </div>
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-amber-700 mt-0.5" />
                          <span>Community-led implementation</span>
                        </div>
                      </div>
                    </div>

                    {/* ✅ Keep button style language */}
                    <div className="px-7 md:px-8 py-5 bg-white border-t border-stone-200">
                      <Button
                        asChild
                        className="bg-amber-600 hover:bg-amber-700 text-white rounded-full px-8 py-6 text-lg w-full"
                      >
                        <Link to="/get-involved#volunteer" data-testid="mission-volunteer-btn">
                          Become a Volunteer
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Vision (small, tight) */}
                <Card
                  className="rounded-3xl border border-stone-200 shadow-sm hover:shadow-xl transition-all overflow-hidden"
                  data-testid="vision-card"
                >
                  <CardContent className="p-0">
                    <div className="p-7 md:p-8 bg-gradient-to-br from-blue-50 via-white to-white">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center shrink-0">
                          <Eye className="w-6 h-6 text-blue-700" />
                        </div>
                        <div>
                          <p className="text-xs tracking-[0.2em] uppercase text-blue-700 font-semibold mb-2">
                            Our Vision
                          </p>
                          <h3 className="font-heading text-xl md:text-2xl font-bold text-stone-900 mb-3 leading-snug">
                            A world where every vulnerable person can thrive.
                          </h3>
                          <p className="text-stone-600 leading-relaxed text-sm md:text-[15px]">
                            Every child and vulnerable person has dignity, protection, opportunity, and access to basic needs—
                            regardless of circumstance.
                          </p>
                        </div>
                      </div>

                      <div className="mt-5 space-y-2.5">
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-blue-700 mt-0.5" />
                          <span>Safe and protected communities</span>
                        </div>
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-blue-700 mt-0.5" />
                          <span>Equal opportunity for all</span>
                        </div>
                        <div className="flex items-start gap-2 text-stone-700 text-sm">
                          <CheckCircle2 className="w-4.5 h-4.5 text-blue-700 mt-0.5" />
                          <span>Sustainable systems that last</span>
                        </div>
                      </div>
                    </div>

                    {/* ✅ Partner button matches footer style (blue) */}
                    <div className="px-7 md:px-8 py-5 bg-white border-t border-stone-200">
                      <Button
                        asChild
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-6 text-lg w-full"
                      >
                        <Link to="/get-involved#partner" data-testid="vision-partner-btn">
                          Become a Partner
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Optional: if you want a donate button here too, tell me and I’ll place it cleanly */}
            </div>
          </div>
        </div>
      </section>

      {/* Motto */}
      <section className="relative overflow-hidden py-20 md:py-24 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-700 via-amber-800 to-orange-900" />
        <div className="absolute inset-0 opacity-20">
          <img src="/assets/mission-motto-bg.png" alt="" className="w-full h-full object-cover" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-20 h-20 bg-white/20 border border-white/30 rounded-full flex items-center justify-center mx-auto mb-8 backdrop-blur-sm">
            <Award className="w-10 h-10" />
          </div>
          <p className="text-sm uppercase tracking-[0.2em] text-amber-200 mb-4">Our Motto</p>
          <h2 className="font-heading text-4xl md:text-5xl font-bold leading-tight">
            Saving Lives, Redirecting Destinies
          </h2>
        </div>
      </section>

      {/* Strategic Pillars */}
      <section className="py-20 md:py-28 bg-gradient-to-b from-stone-50 to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-accent text-xl text-amber-700 mb-3">How We Deliver Impact</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-4">
              Our Strategic Pillars
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Six core focus areas through which we turn compassion into measurable,
              long-term community impact.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, index) => (
              <Card
                key={pillar.title}
                className="rounded-2xl border-stone-200/80 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all bg-white"
                data-testid={`pillar-${index}`}
              >
                <CardContent className="p-6">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <span className="text-amber-700 font-bold">{index + 1}</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-stone-900 mb-2">{pillar.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{pillar.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MissionVision;